import React, { useState } from 'react';
import {
  Box,
  Tooltip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { cogsService } from '../../services/cogsService';

/**
 * Lead Time cell for the Products table.
 *
 * Lead time is BIMODAL — fast local restocks (days) vs overseas production runs (months) — so
 * each SKU shows BOTH: a Restock lead time and a Production lead time, computed from genuine
 * po_date -> arrival pairs (goods receipts + PO actual_delivery_date; GR-OPEN & STUB-PO excluded;
 * split at 60 days). A SKU with no history of its own falls back to the brand median ("~", muted).
 * Hover shows every PO -> arrival pair grouped into restock vs production.
 */
export default function LeadTimeCell({ brandId, product, onEdit }) {
  const rSku = parseInt(product.restock_lead_time_days) || 0;
  const rN = parseInt(product.restock_sample_size) || 0;
  const pSku = parseInt(product.production_lead_time_days) || 0;
  const pN = parseInt(product.production_sample_size) || 0;
  const rBrand = parseInt(product.brand_restock_lead_time_days) || 0;
  const pBrand = parseInt(product.brand_production_lead_time_days) || 0;
  const manual = parseInt(product.lead_time_days) || 0;

  // Per regime: use the SKU's own value when it has observations, else the brand estimate.
  const restock = rN > 0 ? { v: rSku, est: false, n: rN } : (rBrand > 0 ? { v: rBrand, est: true } : null);
  const production = pN > 0 ? { v: pSku, est: false, n: pN } : (pBrand > 0 ? { v: pBrand, est: true } : null);
  const hasOwnData = rN > 0 || pN > 0;

  // Human overrides (from getCogs). These columns are STRINGS: '' = no override, otherwise the
  // value as a string (e.g. '21', and '0' is a legitimate zero-day override — distinct from '').
  // Detect presence by `!== ''` (NOT `!= null` — the no-override value is '', not null).
  const ovrR = product.override_restock_lead_time_days;
  const ovrP = product.override_production_lead_time_days;
  const restockOverridden = ovrR !== null && ovrR !== undefined && ovrR !== '';
  const productionOverridden = ovrP !== null && ovrP !== undefined && ovrP !== '';
  const editable = typeof onEdit === 'function';

  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadHistory = async () => {
    if (loaded || loading || !brandId || !hasOwnData) return;
    setLoading(true);
    try {
      const res = await cogsService.getLeadTimeHistory(brandId, product.sku);
      setHistory(res?.data || []);
    } catch (e) {
      setHistory([]);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  // No genuine data anywhere (e.g. drop-ship brand) -> manual value or dash.
  // (If an override is set we fall through so the override still renders.)
  if (!restock && !production && !restockOverridden && !productionOverridden) {
    if (!manual) return <span>—</span>;
    return (
      <Tooltip arrow title="Manually entered — no purchase-order / goods-receipt history yet.">
        <Box component="span" sx={{ color: 'text.secondary', cursor: 'help' }}>{manual} days</Box>
      </Tooltip>
    );
  }

  const histRows = history || [];
  const restockRows = histRows.filter(h => Number(h.lead_days) < 60);
  const productionRows = histRows.filter(h => Number(h.lead_days) >= 60);

  const histTable = (rows) => (
    <Table size="small" sx={{ '& td': { borderBottom: 'none', px: 0.5, py: 0.15 } }}>
      <TableBody>
        {rows.slice(0, 8).map((h, i) => (
          <TableRow key={i}>
            <TableCell sx={{ color: 'common.white' }}>{h.po_number}</TableCell>
            <TableCell sx={{ color: 'grey.300' }}>{h.po_date}</TableCell>
            <TableCell sx={{ color: 'grey.300' }}>→ {h.arrival_date}</TableCell>
            <TableCell align="right" sx={{ color: 'common.white' }}>{h.lead_days}d</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const tip = (
    <Box sx={{ maxWidth: 400 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
        Lead time — po date → arrival{hasOwnData ? '' : ' (brand average estimate)'}
      </Typography>
      {restock && (
        <Typography variant="caption" sx={{ display: 'block', color: restock.est ? 'grey.400' : 'common.white' }}>
          Restock: {restock.est ? `~${restock.v}d (brand avg)` : `${restock.v}d — median of ${restock.n} restock ${restock.n === 1 ? 'order' : 'orders'} (<60d)`}
        </Typography>
      )}
      {production && (
        <Typography variant="caption" sx={{ display: 'block', color: production.est ? 'grey.400' : 'common.white' }}>
          Production: {production.est ? `~${production.v}d (brand avg)` : `${production.v}d — median of ${production.n} production ${production.n === 1 ? 'run' : 'runs'} (≥60d)`}
        </Typography>
      )}
      {(restockOverridden || productionOverridden) && (
        <Typography variant="caption" sx={{ display: 'block', color: 'warning.light', mt: 0.5, fontWeight: 600 }}>
          Manual override{restockOverridden ? ` — restock ${Math.round(Number(ovrR))}d (computed ${restock ? restock.v : '—'}d)` : ''}{productionOverridden ? ` — production ${Math.round(Number(ovrP))}d (computed ${production ? production.v : '—'}d)` : ''}
        </Typography>
      )}
      {manual > 0 && <Typography variant="caption" sx={{ display: 'block', color: 'grey.500', mt: 0.25 }}>Manually entered: {manual}d</Typography>}
      {loading && <Box sx={{ py: 1, textAlign: 'center' }}><CircularProgress size={16} /></Box>}
      {loaded && hasOwnData && restockRows.length > 0 && (
        <>
          <Typography variant="caption" sx={{ display: 'block', color: 'grey.400', mt: 0.75 }}>Restock orders</Typography>
          {histTable(restockRows)}
        </>
      )}
      {loaded && hasOwnData && productionRows.length > 0 && (
        <>
          <Typography variant="caption" sx={{ display: 'block', color: 'grey.400', mt: 0.5 }}>Production runs</Typography>
          {histTable(productionRows)}
        </>
      )}
    </Box>
  );

  // One line per regime. When overridden, the manual value is primary (amber, bold) and the
  // computed value is shown struck-through+muted ("show both"). An edit pencil opens the dialog.
  const renderRegime = (data, overridden, ovrVal, regimeKey, label) => {
    if (!data && !overridden) return null;
    const computedStr = data ? `${data.est ? '~' : ''}${data.v}d` : '—';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.4 }}>
        {overridden ? (
          <>
            <Box component="span" sx={{ color: 'warning.main', fontWeight: 700, fontSize: '0.8rem' }}>
              {Math.round(Number(ovrVal))}d
            </Box>
            <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.65rem', textDecoration: 'line-through' }}>
              {computedStr}
            </Box>
          </>
        ) : (
          <Box component="span" sx={{ color: data.est ? 'text.secondary' : 'text.primary', fontSize: '0.8rem' }}>
            {computedStr}
          </Box>
        )}
        <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>{label}</Box>
        {editable && (
          <IconButton
            size="small"
            sx={{ p: 0.1, ml: 0.1 }}
            onClick={(e) => { e.stopPropagation(); onEdit(regimeKey, data ? data.v : null, overridden); }}
            aria-label={`Override ${label} lead time`}
          >
            <EditOutlinedIcon sx={{ fontSize: '0.85rem', color: overridden ? 'warning.main' : 'text.disabled' }} />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <Tooltip arrow title={tip} onOpen={loadHistory}>
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          borderBottom: '1px dashed',
          borderColor: (restockOverridden || productionOverridden) ? 'warning.main' : (hasOwnData ? 'primary.main' : 'info.main'),
          cursor: 'help',
          lineHeight: 1.3,
          textAlign: 'right',
        }}
      >
        {renderRegime(restock, restockOverridden, ovrR, 'restock', 'restock')}
        {renderRegime(production, productionOverridden, ovrP, 'production', 'prod')}
      </Box>
    </Tooltip>
  );
}
